// frontend/src/contexts/ConnectivityContext.js
// 軽量なオンライン/オフライン状態管理。navigator.onLine と window の
// 'online'/'offline' イベントを購読し、復帰（offline→online）エッジを1回だけ
// 通知する。Mobile の connectivity.js（connectivity-restored）に相当する
// 「軽い回復」トリガを Web にも提供する。
//
// 復帰時は window CustomEvent 'casticart:connectivity-restored' を1回 dispatch
// し、既存のポーリング系 Provider（MessageCountContext / MaintenanceContext）が
// これを購読して軽く再取得する。エッジ検出はこの Provider に集約し、購読側での
// 二重エッジ判定・連続発火を避ける。
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// SSR/テスト安全に現在のオンライン状態を読む。navigator が無い環境では
// 「オンライン」とみなす（バナーを出さない安全側）。
export const readInitialOnline = () => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
    return true;
  }
  return navigator.onLine;
};

export const CONNECTIVITY_RESTORED_EVENT = 'casticart:connectivity-restored';

const ConnectivityContext = createContext({
  online: true,
  offline: false,
});

export function ConnectivityProvider({ children }) {
  const [online, setOnline] = useState(readInitialOnline);
  // 直近のオンライン状態を保持し、offline→online のエッジのみで復帰通知する。
  const onlineRef = useRef(online);

  const emitRestored = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent(CONNECTIVITY_RESTORED_EVENT));
    } catch (_) {
      // CustomEvent 非対応環境では通知をスキップ（状態自体は更新済み）。
    }
  }, []);

  const applyOnline = useCallback(
    (nextOnline) => {
      const normalized = Boolean(nextOnline);
      if (onlineRef.current === normalized) return;
      onlineRef.current = normalized;
      setOnline(normalized);
      // 復帰エッジ（offline→online）のときだけ一度通知する。
      if (normalized) emitRestored();
    },
    [emitRestored]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return undefined;
    }
    const handleOnline = () => applyOnline(true);
    const handleOffline = () => applyOnline(false);
    // マウント時に実際の状態と同期（イベントを取りこぼしていた場合の補正）。
    applyOnline(readInitialOnline());
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [applyOnline]);

  const value = useMemo(
    () => ({
      online,
      offline: !online,
    }),
    [online]
  );

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity() {
  return useContext(ConnectivityContext);
}
